import clsx from "clsx";
import type { RigaPratica, Segnalazione } from "../../api/statoLavori";
import { SERVIZIO_PRECREDIT_LABELS } from "../../api/devices";
import { Badge } from "../../components/ui/Badge";
import { TONO_STATO, percentuale } from "./presentazione";

/**
 * I pezzi con cui si mostra una pratica in poco spazio: l'elenco di «Stato
 * lavori» e il riquadro della Vista Sede (issue #211, B4) li usano entrambi,
 * così la stessa pratica si legge allo stesso modo nei due posti.
 */

/** Le segnalazioni che fermano il lavoro in rosso, le altre in giallo come nella bozza. */
export function stileSegnalazione(s: Segnalazione): string {
  return s.codice === "bloccata" ? "bg-[#ef4444]/10 text-[#b91c1c]" : "bg-amber-50 text-amber-900";
}

/** Il servizio della pratica, oppure il giallo di «da verificare» quando manca. */
export function ServizioPratica({ servizio, testoDaVerificare = "Da verificare" }: { servizio: RigaPratica["servizio"]; testoDaVerificare?: string }) {
  return servizio.valore ? (
    <Badge color="primary">{SERVIZIO_PRECREDIT_LABELS[servizio.valore]}</Badge>
  ) : (
    <span className="inline-flex rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-900">{testoDaVerificare}</span>
  );
}

/** Il passo a cui è arrivata la pratica, col suo stato. Senza fase tutti i passi sono conclusi. */
export function FasePratica({ fase }: { fase: RigaPratica["fase"] }) {
  return fase ? (
    <>
      <p className="font-semibold text-ink">{fase.titolo}</p>
      <Badge color={TONO_STATO[fase.stato]} className="mt-1.5">
        {fase.dettaglio}
      </Badge>
    </>
  ) : (
    <Badge color="success">Tutti i passi conclusi</Badge>
  );
}

/** La barra dei passi completati su quelli necessari, con i «non necessari» sotto. */
export function AvanzamentoPratica({ avanzamento }: { avanzamento: RigaPratica["avanzamento"] }) {
  return (
    <>
      <div className="flex items-center gap-2.5">
        <div
          className="h-2 flex-1 overflow-hidden rounded bg-[#EDEFEB]"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={avanzamento.necessari}
          aria-valuenow={avanzamento.completati}
          aria-label={`${avanzamento.completati} passi su ${avanzamento.necessari} completati`}
        >
          <div className="h-2 bg-primary" style={{ width: `${percentuale(avanzamento)}%` }} />
        </div>
        <span className="whitespace-nowrap text-[13px] font-semibold">
          {avanzamento.completati} / {avanzamento.necessari}
        </span>
      </div>
      {avanzamento.non_necessari > 0 && (
        <p className="mt-1 text-xs text-ink/60">
          {avanzamento.non_necessari === 1 ? "1 passo non necessario" : `${avanzamento.non_necessari} passi non necessari`}
        </p>
      )}
    </>
  );
}

/** Le segnalazioni della pratica (bloccata, servizio da verificare, promemoria ordini…), una per riga. */
export function SegnalazioniPratica({ segnalazioni }: { segnalazioni: Segnalazione[] }) {
  return (
    <>
      {segnalazioni.map((s, i) => (
        <span key={i} className={clsx("mb-1 block rounded px-2 py-1 text-xs", stileSegnalazione(s))}>
          {s.testo}
        </span>
      ))}
    </>
  );
}

/** Le bozze da confermare della pratica (passi 7 e 9, PR C2), accanto alle segnalazioni. Niente se non ce ne sono. */
export function BozzePratica({ n }: { n: number | undefined }) {
  // Un conteggio mancante vale zero: niente «undefined bozze da confermare».
  if (!n) return null;
  return (
    <span className="mb-1 block rounded bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-900">
      {n === 1 ? "1 bozza da confermare" : `${n} bozze da confermare`}
    </span>
  );
}
